import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, FileSignature, Pencil, Trash2, Building2, FileDown, ArrowRightLeft, PenLine, CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, cx, API } from "@/lib/api";
import { eur, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import DocumentoModal from "@/components/DocumentoModal";

const TIPO_BADGE = {
  RDO: "bg-sky-100 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300",
  ODA: "bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-300",
  CEL: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
};

export default function Documenti() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fTipo, setFTipo] = useState("all");
  const [fCommessa, setFCommessa] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    const data = await api.get("/documenti");
    setItems(data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      const [cant, forn] = await Promise.all([api.get("/cantieri"), api.get("/fornitori")]);
      setCantieri(cant); setFornitori(forn);
    })();
  }, []);

  const confirmDelete = async () => {
    try {
      await api.del(`/documenti/${toDelete.id}`);
      toast.success("Documento eliminato.");
      setToDelete(null); load();
    } catch (err) { toast.error(err.message); }
  };

  const convertiInOda = (d) => {
    setEditing({
      ...d, id: undefined, numero: undefined, tipo: "ODA",
      rif_offerta: d.numero || d.rif_offerta || "",
    });
    setModal(true);
    toast.info(`Conversione ${d.numero} → nuovo ODA. Controlla e salva.`);
  };

  const openPdf = (d) => window.open(`${API}/documenti/${d.id}/pdf`, "_blank");

  const toggleFirma = async (d) => {
    try {
      const res = await api.post(`/documenti/${d.id}/firma`);
      toast.success(res.firmato ? "Documento firmato." : "Firma rimossa.");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const visible = items.filter((d) =>
    (fTipo === "all" || d.tipo === fTipo) &&
    (fCommessa === "all" || d.cantiere_id === fCommessa)
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">RDO / ODA / CEL</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} documenti · richieste d'offerta, ordini e certificati.</p>
        </div>
        <Button onClick={() => { setEditing(null); setModal(true); }} data-testid="add-documento-button"
          className="gap-2 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900">
          <Plus className="h-4 w-4" /> Nuovo Documento
        </Button>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Select value={fTipo} onValueChange={setFTipo}>
              <SelectTrigger data-testid="filter-tipo-select"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i tipi</SelectItem>
                <SelectItem value="RDO">RDO</SelectItem>
                <SelectItem value="ODA">ODA</SelectItem>
                <SelectItem value="CEL">CEL</SelectItem>
              </SelectContent>
            </Select>
            <Select value={fCommessa} onValueChange={setFCommessa}>
              <SelectTrigger data-testid="filter-commessa-select"><SelectValue placeholder="Commessa" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le commesse</SelectItem>
                {cantieri.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-10 rounded bg-secondary animate-pulse" />)}</div>
          ) : visible.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              <FileSignature className="h-8 w-8 mx-auto mb-3 opacity-40" />
              {items.length === 0 ? "Nessun documento. Creane uno per iniziare." : "Nessun documento per i filtri selezionati."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tipo</TableHead><TableHead>Numero</TableHead><TableHead>Data</TableHead>
                    <TableHead>Fornitore</TableHead><TableHead>Commessa</TableHead>
                    <TableHead className="text-right">Totale</TableHead><TableHead className="w-40"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((d) => (
                    <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                      <TableCell><span className={cx("text-xs font-semibold px-2 py-0.5 rounded-full", TIPO_BADGE[d.tipo] || "bg-secondary")}>{d.tipo}</span></TableCell>
                      <TableCell className="font-mono font-medium">{d.numero}{d.firmato && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600" data-testid={`doc-firmato-${d.id}`}><CheckCircle2 className="h-3 w-3" />Firmato</span>}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">{fmtDate(d.data)}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{d.fornitore_nome || "—"}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        <span className="flex items-center gap-1">{d.cantiere_nome ? <><Building2 className="h-3 w-3" />{d.cantiere_nome}</> : "—"}</span>
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold whitespace-nowrap">{eur(d.totale)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-amber-600" data-testid={`pdf-doc-${d.id}`} onClick={() => openPdf(d)} title="Scarica PDF"><FileDown className="h-3.5 w-3.5" /></Button>
                          {d.tipo === "RDO" && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-violet-600" data-testid={`convert-doc-${d.id}`} onClick={() => convertiInOda(d)} title="Converti in ODA"><ArrowRightLeft className="h-3.5 w-3.5" /></Button>}
                          {isOwner && (d.tipo === "ODA" || d.tipo === "CEL") && <Button variant="ghost" size="icon" className={cx("h-8 w-8", d.firmato ? "text-emerald-600" : "text-muted-foreground hover:text-emerald-600")} data-testid={`firma-doc-${d.id}`} onClick={() => toggleFirma(d)} title={d.firmato ? "Rimuovi firma" : "Firma documento"}><PenLine className="h-3.5 w-3.5" /></Button>}
                          {!(d.firmato && (d.tipo === "ODA" || d.tipo === "CEL") && !isOwner) && <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-doc-${d.id}`} onClick={() => { setEditing(d); setModal(true); }}><Pencil className="h-3.5 w-3.5" /></Button>}
                          {isOwner && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" data-testid={`delete-doc-${d.id}`} onClick={() => setToDelete(d)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <DocumentoModal open={modal} onOpenChange={setModal} cantieri={cantieri} fornitori={fornitori} initial={editing} onSaved={load} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il documento?</AlertDialogTitle>
            <AlertDialogDescription>"{toDelete?.numero}" verrà spostato nel cestino.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-doc">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-doc" className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
